import { useQuery } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import ModeEdit from "@mui/icons-material/ModeEdit";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { EpisodicEventItemFragment, WasteEpisodicEventStatus } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { ReactNode, useState } from "react";

import { formatContainerDate } from "../Containers/containerDisplay";
import { EPISODIC_EVENT_MANIFESTS_QUERY } from "./api";
import { DeclarationReviewDialog } from "./DeclarationReviewDialog";
import {
  daysUsedInWindow,
  EVENT_TYPE_LABELS,
  eventStatusChip,
  EVENT_WINDOW_DAYS,
  episodicReliefAdvisory,
  finalizeBlockedReason,
  formatEventWindow,
  isEventWindowLapsed,
  lapsedEventAlertCopy,
  unshippedEventWaste,
} from "./episodicDisplay";
import { episodicDeclarationsEnabled } from "./episodicFlags";
import { containerEventSummary, eventContainerRows } from "./eventContainerRows";
import { EventContainersSection } from "./EventContainersSection";
import { EventManifestsSection } from "./EventManifestsSection";
import { EventSection } from "./EventSection";
import { EventSummaryStrip, ManifestsState } from "./EventSummaryStrip";
import { ObligationsSection } from "./ObligationsSection";

const EVENT_BASIS_CITATION = "40 CFR 262 subpart L";

const NO_CONTAINERS_CAPTION =
  "No containers on this event, so its pounds come from the manifests above. Attaching a container switches the event to container weights: pounds entered above stay in effect but can then only be removed, not changed.";

function EventFieldValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

/** The days-used-of-60 progress bar, with the ship-by deadline right-aligned
 * under it. Turns red once the window has lapsed. */
function WindowProgress({ event, lapsed }: { event: EpisodicEventItemFragment; lapsed: boolean }) {
  const daysUsed = daysUsedInWindow(event.startDate);
  return (
    <Box>
      <LinearProgress
        variant="determinate"
        value={(daysUsed / EVENT_WINDOW_DAYS) * 100}
        color={lapsed ? "error" : "primary"}
        sx={{ mb: 0.5, height: 6, borderRadius: 1 }}
      />
      <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="caption" color="text.secondary">
          {daysUsed} of {EVENT_WINDOW_DAYS} days used
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Ship &amp; remove waste by {formatContainerDate(event.accumulationDeadline)}
        </Typography>
      </Stack>
    </Box>
  );
}

function ReliefAdvisoryAlert({ relief }: { relief: EpisodicEventItemFragment["relief"] }) {
  const advisory = episodicReliefAdvisory(relief);
  return advisory ? <Alert severity="info">{advisory.body}</Alert> : null;
}

/**
 * The lapsed-window alert is `error`. The relief advisory and the
 * zero-container nudge stay `info`: neither is a non-compliance verdict.
 */
function EventStatusAlerts({
  event,
  isOpen,
  lapsed,
  nudgeContainers,
}: {
  event: EpisodicEventItemFragment;
  isOpen: boolean;
  lapsed: boolean;
  /** Off where the event's pounds come from manifests, whose Containers section says why it is empty. */
  nudgeContainers: boolean;
}) {
  return (
    <>
      {lapsed && (
        <Alert severity="error">
          {lapsedEventAlertCopy(event.accumulationDeadline, unshippedEventWaste(event.containers))}
        </Alert>
      )}
      {nudgeContainers && isOpen && event.containers.length === 0 && (
        <Alert severity="info">No containers are attached to this event.</Alert>
      )}
    </>
  );
}

function EventMoreMenu({ onDelete }: { onDelete?: () => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        aria-label="More event actions"
        aria-haspopup="menu"
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        <MenuItem
          disabled={!onDelete}
          onClick={() => {
            setAnchor(null);
            onDelete?.();
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "inherit" }}>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete event</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

function EventCardHeader({
  event,
  isActive,
  isOpen,
  showContainers,
  onEdit,
  onFinalize,
  onReopen,
  onDelete,
}: {
  event: EpisodicEventItemFragment;
  isActive: boolean;
  isOpen: boolean;
  showContainers: boolean;
  onEdit?: () => void;
  onFinalize?: () => void;
  onReopen?: () => void;
  onDelete?: () => void;
}) {
  const statusChip = eventStatusChip(event);
  const finalizeBlocked = finalizeBlockedReason(unshippedEventWaste(event.containers));
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      flexWrap="wrap"
      gap={1.5}
    >
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="h6">{event.reason}</Typography>
        {showContainers && <Chip size="small" color={statusChip.color} label={statusChip.label} />}
        <Chip size="small" label={EVENT_TYPE_LABELS[event.type]} />
        <Typography variant="caption" color="text.secondary">
          {EVENT_BASIS_CITATION}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {isActive && (
          <Button size="small" startIcon={<ModeEdit />} onClick={onEdit} disabled={!onEdit}>
            Edit
          </Button>
        )}
        {showContainers && isOpen && (
          // A disabled button fires no hover events, so the tooltip wraps a span.
          <Tooltip title={finalizeBlocked ?? ""} disableHoverListener={finalizeBlocked === null}>
            <span>
              <Button
                size="small"
                onClick={onFinalize}
                disabled={!onFinalize || finalizeBlocked !== null}
              >
                Finalize event
              </Button>
            </span>
          </Tooltip>
        )}
        {showContainers && !isOpen && (
          <Button size="small" onClick={onReopen} disabled={!onReopen}>
            Reopen event
          </Button>
        )}
        <EventMoreMenu onDelete={onDelete} />
      </Stack>
    </Stack>
  );
}

function manifestsState(fetched: boolean, loaded: boolean, failed: boolean): ManifestsState {
  if (!fetched || loaded) {
    return "loaded";
  }
  return failed ? "error" : "loading";
}

function ContainerVariantSections({
  event,
  manifestsEnabled,
}: {
  event: EpisodicEventItemFragment;
  manifestsEnabled: boolean;
}) {
  const { data, error } = useQuery(EPISODIC_EVENT_MANIFESTS_QUERY, {
    variables: { id: event.id },
    skip: !manifestsEnabled,
  });
  const [reviewing, setReviewing] = useState<string | null>(null);
  const exemptions = data?.episodicEvent?.manifestExemptions ?? null;
  const reviewedManifest = exemptions?.manifests.find(
    (manifest) => manifest.manifestTrackingNumber === reviewing
  );
  const manifests = manifestsState(manifestsEnabled, exemptions !== null, error !== undefined);
  return (
    <>
      <EventSummaryStrip
        summary={containerEventSummary(event.containerHistory, exemptions)}
        manifests={manifests}
        showManifested={manifestsEnabled}
        reliefApplies={episodicReliefAdvisory(event.relief) === null}
      />
      <EventContainersSection
        event={event}
        rows={eventContainerRows(event.containerHistory, exemptions)}
        showManifests={manifestsEnabled}
        manifests={manifests}
        onReviewEntered={setReviewing}
      />
      {reviewedManifest && (
        <DeclarationReviewDialog
          eventReason={event.reason}
          manifest={reviewedManifest}
          onClose={() => setReviewing(null)}
        />
      )}
    </>
  );
}

/**
 * Detail card for one episodic event of any status. The obligations section
 * stays for every status: an unmet obligation is still owed after the window
 * ends.
 */
export function EpisodicEventCard({
  event,
  onEdit,
  onFinalize,
  onReopen,
  onDelete,
}: {
  event: EpisodicEventItemFragment;
  onEdit?: () => void;
  onFinalize?: () => void;
  onReopen?: () => void;
  onDelete?: () => void;
}) {
  const { tenantId } = useTenant();
  const { featureFlags } = useFeatureFlags();
  const stepperEnabled = featureFlags[FeatureFlagKey.WasteSubmissionStepper] === true;
  const showContainers = featureFlags[FeatureFlagKey.ContainerTracking] === true;
  const declarationsEnabled = episodicDeclarationsEnabled(featureFlags);
  // A container tenant's event counts from its containers once one is on its record; before that
  // its pounds come from manifests, as on a tenant without container tracking.
  const containerWeighed = showContainers && !event.declarationsWritable;
  const isActive = event.status === WasteEpisodicEventStatus.Active;
  const isOpen = event.status !== WasteEpisodicEventStatus.Finalized;
  const lapsed = isOpen && isEventWindowLapsed(event.accumulationDeadline);
  const showWindowProgress = isOpen && showContainers && event.containers.length > 0;

  return (
    // Matches the container detail page's SectionCard chrome: a flat white
    // tile sitting on the page's canvas background.
    <Card elevation={0}>
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <EventCardHeader
            event={event}
            isActive={isActive}
            isOpen={isOpen}
            showContainers={showContainers}
            onEdit={onEdit}
            onFinalize={onFinalize}
            onReopen={onReopen}
            onDelete={onDelete}
          />

          {/* Sized to the values, so a long window line stays on one line and
              no empty column is reserved beside it. */}
          <Stack direction="row" flexWrap="wrap" columnGap={6} rowGap={2} useFlexGap>
            {containerWeighed ? (
              <>
                <EventFieldValue label="Event window">
                  {formatEventWindow(event.startDate, event.endDate)}
                </EventFieldValue>
                <EventFieldValue label="Removal deadline">
                  {formatContainerDate(event.accumulationDeadline)}
                </EventFieldValue>
              </>
            ) : (
              <>
                <EventFieldValue label="Start date">
                  {formatContainerDate(event.startDate)}
                </EventFieldValue>
                <EventFieldValue label="Qualifying manifests">
                  Shipped on or after {formatContainerDate(event.startDate)} and through{" "}
                  {formatContainerDate(event.accumulationDeadline)}
                </EventFieldValue>
              </>
            )}
          </Stack>

          {showWindowProgress && <WindowProgress event={event} lapsed={lapsed} />}

          {/* Every tenant: declarations here are stored but exempt nothing, which the customer should know before declaring. */}
          <ReliefAdvisoryAlert relief={event.relief} />
          {showContainers && (
            <EventStatusAlerts
              event={event}
              isOpen={isOpen}
              lapsed={lapsed}
              nudgeContainers={containerWeighed}
            />
          )}

          <ObligationsSection
            event={event}
            tenantId={tenantId ?? ""}
            stepperEnabled={stepperEnabled}
          />

          {containerWeighed && (
            <ContainerVariantSections event={event} manifestsEnabled={declarationsEnabled} />
          )}
          {!containerWeighed && declarationsEnabled && (
            <EventManifestsSection event={event} collapsible={showContainers} />
          )}
          {showContainers && !containerWeighed && (
            <EventSection title="Containers" caption={NO_CONTAINERS_CAPTION}>
              <EventContainersSection
                event={event}
                rows={[]}
                showManifests={false}
                onReviewEntered={() => {}}
              />
            </EventSection>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
